// Fila first-party para telemetria de UX.
// - Buffer em memória, flush por (a) tamanho 20, (b) timer 10s, (c) visibilitychange/pagehide.
// - Usa navigator.sendBeacon para garantir entrega ao fechar/trocar de aba.
// - Fallback fetch+keepalive quando sendBeacon não está disponível.
// - Sampling configurável por categoria de evento (eventos críticos sempre 100%).

import { getSessionId, getDeviceContext, getSessionStartedAt } from './session';

const FLUSH_MS = 10_000;
const MAX_BATCH = 20;
const MAX_BUFFER = 200;

const BACKEND_URL = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '');
const ENDPOINT = `${BACKEND_URL}/ux/ingest`;

// Eventos que NUNCA são amostrados (críticos para entender o produto).
const ALWAYS_KEEP = /^(screen_view|web_vital|js_error|unhandled_rejection|fetch_error|error_boundary|payment_|triage_|prontuario_|terms_|name_|email_|cpf_|phone_|home_cta_|chat_redirected_|consulta_concluida_|rage_click|dead_click|form_friction|attach_|chat_file_click|chat_image_lightbox)/;

// Sampling padrão por categoria (1.0 = 100%).
const SAMPLING = {
  scroll_depth: 0.5,
  pointer_idle: 0.3,
};

const buffer = [];
let timer = null;
let initialized = false;

// Execuções sintéticas (Playwright/WebdriverIO, incluindo suítes rodando
// CONTRA PRODUÇÃO via test:e2e:prod) não devem poluir o ux_events real.
// Investigação 20/07/2026: 129k eventos com app_version 'test-*'/'e2e' na
// coleção; triage_finalize_blocked aparecia como 111/24h no /admin/health
// sendo 109 sintéticos. Os stubs de rota dos specs não seguram sendBeacon,
// então o corte é aqui na fonte. Cobre: navigator.webdriver (automação),
// __E2E__/__SKIP_CACHE_BUST__ (flags dos specs) e APP_VERSION sintética
// que o index.html gera sob essas flags ('e2e' / 'test-NNNNNN').
const isSyntheticRun = () => {
  try {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return true;
    if (typeof window === 'undefined') return false;
    if (window.__E2E__ || window.__SKIP_CACHE_BUST__) return true;
    const v = String(window.APP_VERSION || '');
    return v === 'e2e' || v.startsWith('test-');
  } catch {
    return false;
  }
};

const baseEnvelope = (event) => ({
  ...event,
  session_id: getSessionId(),
  session_started_at: getSessionStartedAt(),
  device: getDeviceContext(),
  ts: event.ts || Date.now(),
});

// Flush imediato com motivo (debug). Usado para garantir que eventos críticos
// (ex.: screen_view de /pagamento, que redireciona logo em seguida para o
// gateway de pagamento) saiam antes da navegação. Internamente é o mesmo
// `flush()` — mas o nome separado deixa a intenção explícita nos call sites.
export function flushSync(_reason) {
  flush();
}

export function flush() {
  if (!buffer.length || typeof window === 'undefined') return;
  const batch = buffer.splice(0, buffer.length);
  const body = JSON.stringify({ events: batch });

  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
  } catch {}

  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Re-enfileira até o limite para tentar de novo no próximo flush.
      if (buffer.length + batch.length <= MAX_BUFFER) {
        buffer.unshift(...batch);
      }
    });
  } catch {
    if (buffer.length + batch.length <= MAX_BUFFER) {
      buffer.unshift(...batch);
    }
  }
}

export const enqueue = (event) => {
  if (!event || !event.name) return;
  if (isSyntheticRun()) return;

  // Sampling: descartar evento amostrado se não for crítico.
  if (!ALWAYS_KEEP.test(event.name)) {
    const rate = SAMPLING[event.name];
    if (typeof rate === 'number' && Math.random() > rate) return;
  }

  if (buffer.length >= MAX_BUFFER) {
    // Buffer transbordando: descarta o evento mais antigo para não vazar memória.
    buffer.shift();
  }
  buffer.push(baseEnvelope(event));
  if (buffer.length >= MAX_BATCH) flush();
};

export const initQueue = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  timer = setInterval(flush, FLUSH_MS);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);

  // Smoke-test: ao iniciar a fila, enfileira um evento `telemetry_alive` que
  // gera bem 1 ingest no /ux/ingest na primeira janela de FLUSH_MS. Se o
  // servidor nao registrar nenhum hit deste evento para um patientId ativo
  // que viu /registration/start, sabemos que a telemetria do cliente esta
  // bloqueada (adblocker, SW velho, WebView restrita) e podemos diagnosticar
  // sem precisar reabrir todo o caso (ex.: vanessacamargo2109 14/05 — 0
  // ux_events para 8 patientIds em 47min, foi a unica pista de que o app
  // dela nao estava enviando telemetria, mas tivemos que descobrir do dificil).
  try {
    enqueue({
      name: 'telemetry_alive',
      params: {
        ua_native: typeof navigator !== 'undefined' && /ConsultaMaisApp/.test(navigator.userAgent || '') ? 1 : 0,
        endpoint: ENDPOINT.replace(/^https?:\/\/[^/]+/, ''),
      },
    });
  } catch {}
};

export const __resetForTests = () => {
  buffer.length = 0;
  if (timer) { clearInterval(timer); timer = null; }
  initialized = false;
};
